// Profile releases table for one Chromium major.
//
// Shows every published `profiles.tar.gz` for the given major plus the
// fleet hosts where each version is installed. Activate / delete are
// powered by the shared mutation hook so this surface stays in sync with
// the legacy /releases/profiles list. Rendered with the shared @veil/ui
// DataTable stack; the version column sorts by semver.

import {
	type ColumnDef,
	getCoreRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	type SortingState,
	useReactTable,
} from "@tanstack/react-table";
import { compareSemver } from "@veil/shared/version";
import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import { Skeleton } from "@veil/ui/components/skeleton";
import { DataTable, DataTableColumnHeader } from "@veil/ui/data-table";
import { formatBytes } from "@veil/ui/lib/format";
import { CheckCircle2, Plus, Trash2 } from "lucide-react";
import { lazy, Suspense, useMemo, useState } from "react";
import { Link } from "wouter";
import type { FleetSnapshot, ProfileReleaseRow } from "@/lib/releases/types.ts";
import { useProfileReleaseMutations } from "@/lib/releases/use-profile-release-mutations.ts";

const DeleteReleaseDialog = lazy(
	() => import("@/components/delete-release-dialog"),
);

interface Props {
	major: number;
	releases: ProfileReleaseRow[];
	loading: boolean;
	canPublish: boolean;
	fleetHosts: Array<{ hostname: string; snapshot: FleetSnapshot | null }>;
}

export function ProfileReleasesTable({
	major,
	releases,
	loading,
	canPublish,
	fleetHosts,
}: Props) {
	const [deleteTarget, setDeleteTarget] = useState<ProfileReleaseRow | null>(
		null,
	);
	const [sorting, setSorting] = useState<SortingState>([
		{ id: "version", desc: true },
	]);
	const { activate, remove } = useProfileReleaseMutations({
		onDeleted: () => setDeleteTarget(null),
	});

	const rows = useMemo(
		() => releases.filter((r) => r.browserMajor === major),
		[releases, major],
	);

	const installedHostsByKey = useMemo(() => {
		const out = new Map<string, string[]>();
		for (const h of fleetHosts) {
			const installed = h.snapshot?.installedProfiles?.[String(major)] ?? [];
			for (const v of installed) {
				const stripped = v.startsWith("v") ? v.slice(1) : v;
				if (!out.has(stripped)) out.set(stripped, []);
				const arr = out.get(stripped);
				if (arr && !arr.includes(h.hostname)) arr.push(h.hostname);
			}
		}
		return out;
	}, [fleetHosts, major]);

	function hostsFor(r: ProfileReleaseRow): string[] {
		return (
			installedHostsByKey.get(`${r.version}-${r.slug}`) ??
			installedHostsByKey.get(r.version) ??
			[]
		);
	}

	const columns = useMemo<ColumnDef<ProfileReleaseRow>[]>(
		() => [
			{
				id: "version",
				accessorKey: "version",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Version" />
				),
				cell: ({ row }) => (
					<span className="font-mono text-xs">
						<Link
							href={`/releases/profiles/${row.original.id}`}
							className="hover:underline"
						>
							{row.original.version}
						</Link>
						{row.original.isActive ? (
							<CheckCircle2
								aria-label="active"
								className="ml-1 inline size-3.5 text-emerald-500"
							/>
						) : null}
					</span>
				),
				sortingFn: (a, b) =>
					compareSemver(a.original.version, b.original.version),
				enableHiding: false,
			},
			{
				id: "slug",
				accessorKey: "slug",
				header: "Slug",
				cell: ({ row }) => (
					<span className="font-mono text-xs">{row.original.slug}</span>
				),
				enableSorting: false,
			},
			{
				id: "description",
				header: "Description",
				cell: ({ row }) => (
					<span className="block max-w-xs truncate text-xs text-muted-foreground">
						{row.original.description ?? "—"}
					</span>
				),
				enableSorting: false,
			},
			{
				id: "fileCount",
				accessorKey: "fileCount",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Files" />
				),
				cell: ({ row }) => (
					<span className="font-mono tabular-nums text-xs">
						{row.original.fileCount.toLocaleString()}
					</span>
				),
			},
			{
				id: "size",
				accessorKey: "sizeBytes",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Size" />
				),
				cell: ({ row }) => (
					<span className="font-mono tabular-nums text-xs">
						{formatBytes(row.original.sizeBytes)}
					</span>
				),
			},
			{
				id: "workers",
				header: "Workers",
				cell: ({ row }) => {
					const hosts = hostsFor(row.original);
					if (hosts.length === 0) {
						return <span className="text-xs text-muted-foreground">—</span>;
					}
					return (
						<div className="flex flex-wrap gap-1">
							{hosts.slice(0, 3).map((h) => (
								<Badge
									key={h}
									variant="outline"
									className="font-mono text-[10px]"
								>
									{h}
								</Badge>
							))}
							{hosts.length > 3 ? (
								<Badge variant="outline" className="text-[10px]">
									+{hosts.length - 3}
								</Badge>
							) : null}
						</div>
					);
				},
				enableSorting: false,
			},
			{
				id: "actions",
				header: () => <div className="text-right">Actions</div>,
				cell: ({ row }) => {
					const r = row.original;
					return (
						<div className="flex items-center justify-end gap-2">
							<Button asChild size="sm" variant="ghost">
								<Link href={`/releases/profiles/${r.id}`}>Inspect</Link>
							</Button>
							{canPublish ? (
								<>
									<Button
										size="sm"
										variant={r.isActive ? "secondary" : "outline"}
										disabled={r.isActive || activate.isPending}
										onClick={() => activate.mutate(r.id)}
									>
										{r.isActive ? "Active" : "Activate"}
									</Button>
									<Button
										size="sm"
										variant="ghost"
										aria-label="Delete release"
										disabled={remove.isPending}
										onClick={() => setDeleteTarget(r)}
									>
										<Trash2 className="size-3.5" />
									</Button>
								</>
							) : null}
						</div>
					);
				},
				enableSorting: false,
				enableHiding: false,
			},
		],
		// hostsFor closes over installedHostsByKey; activate/remove are stable
		// mutation objects from TanStack Query.
		[installedHostsByKey, canPublish, activate.isPending, remove.isPending],
	);

	const table = useReactTable({
		data: rows,
		columns,
		state: { sorting },
		onSortingChange: setSorting,
		getRowId: (row) => row.id,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		initialState: { pagination: { pageSize: 50 } },
	});

	const active = rows.find((r) => r.isActive);

	return (
		<>
			<Card>
				<CardHeader>
					<div className="flex items-start justify-between gap-2">
						<div>
							<CardTitle className="flex items-center gap-2 text-base">
								Profile releases
								{active ? (
									<Badge variant="success">
										active {active.version}-{active.slug}
									</Badge>
								) : rows.length > 0 ? (
									<Badge variant="warning">no active version</Badge>
								) : null}
							</CardTitle>
							<CardDescription>
								{rows.length} release{rows.length === 1 ? "" : "s"} for this
								major
							</CardDescription>
						</div>
						{canPublish && (
							<Button asChild size="sm" variant="outline">
								<Link href={`/releases/new/profiles?browserMajor=${major}`}>
									<Plus className="mr-1 size-3.5" /> Publish
								</Link>
							</Button>
						)}
					</div>
				</CardHeader>
				<CardContent>
					{loading ? (
						<Skeleton className="h-24 w-full" />
					) : rows.length === 0 ? (
						<div className="text-sm text-muted-foreground">
							No profile releases for this major yet.
						</div>
					) : (
						<DataTable table={table} />
					)}
				</CardContent>
			</Card>

			{deleteTarget ? (
				<Suspense fallback={null}>
					<DeleteReleaseDialog
						open
						onOpenChange={(o) => {
							if (!o) setDeleteTarget(null);
						}}
						release={deleteTarget}
						fleetHostsInstalled={hostsFor(deleteTarget)}
						confirming={remove.isPending}
						onConfirm={(force) => remove.mutate({ id: deleteTarget.id, force })}
					/>
				</Suspense>
			) : null}
		</>
	);
}
